import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_CONNECTIVITY_ENDPOINTS = [
	'https://www.google.com/generate_204',
	'https://cloudflare.com/cdn-cgi/trace',
];

const PING_TIMEOUT_MS = 3000;
const RECOVERY_CHECK_INTERVAL_MS = 30000;

const getNavigatorOnline = () => {
	if (typeof navigator === 'undefined') return true;
	return navigator.onLine;
};

const now = () => Date.now();

// Module-level state (migrated from hook)
let endpoints = [...DEFAULT_CONNECTIVITY_ENDPOINTS];
let listeners = new Set();
let started = false;
let checkInFlight = null;
let recoveryIntervalId = null;

let state = {
	isOnline: getNavigatorOnline(),
	isReallyOnline: true,
	lastFailedRequest: null,
	consecutiveFailures: 0,
};

export function configure({ endpoints: cfgEndpoints } = {}) {
	if (Array.isArray(cfgEndpoints) && cfgEndpoints.length) {
		endpoints = cfgEndpoints.filter(Boolean);
	}
}

export function getState() {
	return { ...state };
}

export function subscribe(listener) {
	if (typeof listener !== 'function') {
		return () => {};
	}

	start();
	listeners.add(listener);
	listener(getState());

	return () => listeners.delete(listener);
}

export function start() {
	if (started || typeof window === 'undefined') return;

	started = true;
	window.addEventListener('online', handleOnline);
	window.addEventListener('offline', handleOffline);

	state = {
		...state,
		isOnline: getNavigatorOnline(),
	};

	emit();
	void checkInternet({ reason: 'start', force: true });
}

export function stop() {
	if (!started || typeof window === 'undefined') return;

	started = false;
	window.removeEventListener('online', handleOnline);
	window.removeEventListener('offline', handleOffline);
	clearRecoveryMonitor();
}

function handleOnline() {
	state = {
		...state,
		isOnline: true,
	};
	emit();

	void checkInternet({ reason: 'online', force: true });
}

function handleOffline() {
	applyOfflineState();
}

export function isLikelyNetworkError(error) {
	if (!error) return false;

	return (
		error.code === 'NETWORK_ERROR' ||
		error.code === 'ECONNABORTED' ||
		error.code === 'ETIMEDOUT' ||
		error.code === 'ERR_NETWORK' ||
		error.code === 'ERR_CONNECTION_REFUSED' ||
		error.code === 'ERR_CONNECTION_RESET' ||
		error.message?.toLowerCase().includes('network') ||
		error.message?.toLowerCase().includes('timeout') ||
		error.message?.toLowerCase().includes('failed to fetch') ||
		!error.response
	);
}

export function reportRequestSuccess() {
	start();
	applySuccessState();
}

export async function reportRequestFailure({ error } = {}) {
	start();

	if (!isLikelyNetworkError(error)) {
		applySuccessState();
		return getState();
	}

	const hasInternet = await probeConnectivity();

	if (hasInternet) {
		// Интернет в целом доступен, но запрос завершился сетевой ошибкой
		state = {
			...state,
			isOnline: getNavigatorOnline(),
			isReallyOnline: false,
			lastFailedRequest: now(),
			consecutiveFailures: (state.consecutiveFailures || 0) + 1,
		};
		emit();
		return getState();
	}

	// Интернет недоступен подтверждено ping-check.
	applyFailureState({
		timestamp: now(),
		isOnline: false,
	});

	return getState();
}

export async function checkInternet({ force = false } = {}) {
	start();

	if (!getNavigatorOnline()) {
		applyOfflineState();
		return getState();
	}

	if (checkInFlight && !force) {
		return checkInFlight;
	}

	checkInFlight = (async () => {
		const hasInternet = await probeConnectivity();

		if (hasInternet) {
			applySuccessState();
		} else {
			applyFailureState({
				timestamp: now(),
				isOnline: false,
			});
		}

		return getState();
	})();

	try {
		return await checkInFlight;
	} finally {
		checkInFlight = null;
	}
}

async function probeConnectivity() {
	const eps = endpoints.length ? endpoints : DEFAULT_CONNECTIVITY_ENDPOINTS;

	for (const endpoint of eps) {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

		try {
			await fetch(endpoint, {
				method: 'HEAD',
				mode: 'no-cors',
				cache: 'no-cache',
				signal: controller.signal,
			});

			clearTimeout(timeoutId);
			return true;
		} catch {
			clearTimeout(timeoutId);
		}
	}

	return false;
}

function applySuccessState() {
	state = {
		...state,
		isOnline: getNavigatorOnline(),
		isReallyOnline: true,
		lastFailedRequest: null,
		consecutiveFailures: 0,
	};

	clearRecoveryMonitor();
	emit();
}

function applyFailureState({ timestamp, isOnline } = {}) {
	state = {
		...state,
		isOnline: isOnline ?? getNavigatorOnline(),
		isReallyOnline: false,
		lastFailedRequest: timestamp ?? now(),
		consecutiveFailures: (state.consecutiveFailures || 0) + 1,
	};

	ensureRecoveryMonitor();
	emit();
}

function applyOfflineState() {
	state = {
		...state,
		isOnline: false,
		isReallyOnline: false,
		lastFailedRequest: state.lastFailedRequest ?? now(),
	};

	ensureRecoveryMonitor();
	emit();
}

function ensureRecoveryMonitor() {
	if (recoveryIntervalId) return;

	recoveryIntervalId = setInterval(() => {
		void checkInternet({ force: false });
	}, RECOVERY_CHECK_INTERVAL_MS);
}

function clearRecoveryMonitor() {
	if (!recoveryIntervalId) return;
	clearInterval(recoveryIntervalId);
	recoveryIntervalId = null;
}

function emit() {
	const snapshot = getState();
	listeners.forEach((listener) => {
		try {
			listener(snapshot);
		} catch {
			// noop
		}
	});
}

const getDefaultNetworkStatus = () => ({
	isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
	isReallyOnline: true,
	lastFailedRequest: null,
	consecutiveFailures: 0,
});

const NetworkStatusLamp = () => {
	const [safeStatus, setSafeStatus] = useState(() => getState() || getDefaultNetworkStatus());

	useEffect(() => {
		const unsubscribe = subscribe((next) => setSafeStatus(next));
		// Ensure background monitoring starts
		void checkInternet({ force: true });

		return () => {
			try {
				unsubscribe();
			} catch {
				// noop
			}
		};
	}, []);

	const actuallyOnline = safeStatus.isOnline && safeStatus.isReallyOnline;
	const hasNetworkIssues = safeStatus.isOnline && !safeStatus.isReallyOnline;

	const getStatusText = () => {
		if (!safeStatus.isOnline) return 'Вы офлайн';
		if (hasNetworkIssues) {
			const failures = safeStatus.consecutiveFailures;
			return `Проблемы с интернетом${failures > 1 ? ` (${failures} ошибок)` : ''}`;
		}
		return 'Вы онлайн';
	};

	return (
		<LampWrapper
			$isOnline={actuallyOnline}
			$hasIssues={hasNetworkIssues}
			title={getStatusText()}
		/>
	);
};

const LampWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 5px;
	width: 5px;
	border-radius: 50%;
	background-color: ${(props) => {
		if (props.$hasIssues) return '#ff0000';
		return props.$isOnline ? '#41bb00' : '#ff0000';
	}};
	box-shadow: 0 0 10px 1px ${(props) => {
		if (props.$hasIssues) return '#ff0000';
		return props.$isOnline ? '#41bb00' : '#ff0000';
	}};
	transition: background-color 0.3s, box-shadow 0.3s;
	cursor: help;
`;

export default NetworkStatusLamp;
